import { useMemo, useState } from 'react'
import { Abrufausfall, useAbruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Auswahlfeld } from './Auswahlfeld'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { ANFANG, periodenname, t } from './sprache'
import { Blattabschnitt, wertwort } from './Stammdaten'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import type { Listeneintrag } from './typen'

/** Der feste Vorrat, in der Reihenfolge der Auswahl (R1523). */
const RHYTHMEN = ['monatlich', 'quartalsweise', 'halbjaehrlich', 'jaehrlich'] as const

/** Solange die Zeilen nicht da sind, steht keine Zeile da (R1210). */
const KEINE: Listeneintrag[] = []

/**
 * **Der erwartete Rhythmus der Datenlieferung** — im Blatt der Gesellschaft
 * (2.12 Abschnitt 3.27, R1523, R1524).
 *
 * **Eine Zeile ab einem Monat, wie die Kreiszugehörigkeit** (Entscheidung des
 * Auftraggebers vom 2026-10-11): Wechselt eine Gesellschaft ab Juli 2027 von
 * quartalsweise auf monatlich, bleibt die Zeit davor richtig bewertet. Ab
 * Werk steht eine Zeile „monatlich ab Anfang" da (R1524).
 *
 * **Der Hinweistext steht unter dem Namen**, im Stil der Meldungen (7.1
 * Abschnitt 13): was das Feld bewirkt und woran man den richtigen Wert
 * erkennt.
 *
 * **Geändert wird ab einem Monat, nicht in der Zeile.** Eine neue Zeile mit
 * späterem „gilt ab" löst die frühere für die Zeit danach ab. Entfernt werden
 * kann jede Zeile außer der ersten — ohne sie gälte „monatlich" stillschweigend.
 */
export function Lieferrhythmen({ einheit, aufAenderung }: { einheit: string; aufAenderung: () => void }) {
  // Die Spaltenbreiten zieht der Anwender, wie in jeder Tabelle (7.3 Abschnitt 10, R415, R833).
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('lieferrhythmen:lieferrhythmen')
  const darf = useDarf()
  const [stand, setStand] = useState(0)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [aendern, setAendern] = useState(false)
  const [entwurf, setEntwurf] = useState<{ rhythmus: string; ab: number | null }>({ rhythmus: '', ab: null })

  // Die Zeilen sind der Inhalt des Abschnitts — ein Hauptabruf (R1211).
  const abruf = useAbruf(
    () =>
      api.stammdatenliste('lieferrhythmus').then((l) => l.eintraege.filter((e) => e.werte.einheit === einheit)),
    [einheit, stand],
  )
  const zeilen = useMemo(
    () => [...(abruf.wert ?? KEINE)].sort((a, b) => Number(a.werte.gueltig_von) - Number(b.werte.gueltig_von)),
    [abruf.wert],
  )
  const erste = zeilen[0]?.schluessel

  const auswahl = useMemo(() => RHYTHMEN.map((r) => ({ wert: r, text: wertwort('rhythmus', r) })), [])

  async function tue(was: () => Promise<unknown>) {
    setLaeuft(true)
    setMeldung(null)

    try {
      await was()
      setStand((n) => n + 1)
      setAendern(false)
      setEntwurf({ rhythmus: '', ab: null })
      aufAenderung()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Blattabschnitt kennung="einheit.lieferrhythmus" titel={t('stammdaten.feld.rhythmus')}>
      <p className="erlaeuterung">{t('stammdaten.zweck.lieferrhythmus.rhythmus')}</p>

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="lieferrhythmen ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th>{t('lieferrhythmus.spalte.ab')}</th>
              <th>{t('stammdaten.feld.rhythmus')}</th>
              {darf('aussondern') && <th />}
            </tr>
          </thead>
          <tbody>
            {abruf.ausgefallen && (
              <tr>
                <td colSpan={3}>
                  <Abrufausfall abruf={abruf} />
                </td>
              </tr>
            )}
            {abruf.wert !== null && zeilen.length === 0 && (
              <tr>
                <td colSpan={3} className="leerhinweis">
                  {t('lieferrhythmus.leer')}
                </td>
              </tr>
            )}
            {zeilen.map((z) => (
              <tr key={z.schluessel}>
                <td>{periodenname(Number(z.werte.gueltig_von || ANFANG))}</td>
                <td>{wertwort('rhythmus', z.werte.rhythmus ?? '')}</td>
                {darf('aussondern') && (
                  <td className="handgriff">
                    {z.schluessel !== erste && (
                      <button
                        type="button"
                        className="schmal gefahr"
                        disabled={laeuft}
                        onClick={() =>
                          void tue(() =>
                            api.aussondern('lieferrhythmus', z.schluessel, { weg: 'entfernen', abPeriode: null, ziel: null }),
                          )
                        }
                      >
                        {t('lieferrhythmus.entfernen')}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Erst auf Verlangen (R315): Die Liste wird gelesen, nicht gepflegt. */}
      {darf('stammdatumAnlegen') && !aendern && (
        <div className="knopfreihe">
          <button type="button" disabled={laeuft} onClick={() => setAendern(true)}>
            {t('lieferrhythmus.aendern')}
          </button>
        </div>
      )}

      {darf('stammdatumAnlegen') && aendern && (
        <div className="stammdatenfelder lieferrhythmusanlage">
          <label>
            <span className="name">{t('stammdaten.feld.rhythmus')}</span>
            <Auswahlfeld
              wert={entwurf.rhythmus}
              eintraege={auswahl}
              sorte="aufzaehlung"
              leer={t('lieferrhythmus.waehlen')}
              beschriftung={t('stammdaten.feld.rhythmus')}
              aufWahl={(rhythmus) => setEntwurf((e) => ({ ...e, rhythmus }))}
            />
          </label>

          <Periodenfeld
            klasse=""
            wert={entwurf.ab}
            beschriftung={t('lieferrhythmus.spalte.ab')}
            erlaeuterung={t('lieferrhythmus.ab.hinweis')}
            aufWert={(ab) => setEntwurf((e) => ({ ...e, ab }))}
          />

          <div className="aktionsreihe">
            <button
              type="button"
              className="haupt"
              disabled={laeuft || entwurf.rhythmus === '' || entwurf.ab === null}
              onClick={() =>
                void tue(() =>
                  // **Ohne Code**: Der Schlüssel entsteht aus Gesellschaft und „gilt ab" (R559).
                  api.stammdatumAnlegen('lieferrhythmus', '', {
                    felder: { einheit, gueltig_von: String(entwurf.ab), rhythmus: entwurf.rhythmus },
                  }),
                )
              }
            >
              {t('lieferrhythmus.speichern')}
            </button>
            <button
              type="button"
              disabled={laeuft}
              onClick={() => {
                setAendern(false)
                setEntwurf({ rhythmus: '', ab: null })
                setMeldung(null)
              }}
            >
              {t('lieferrhythmus.abbrechen')}
            </button>
          </div>
        </div>
      )}

      {meldung && <p className="meldung">{meldung}</p>}
    </Blattabschnitt>
  )
}
